import React, { useEffect, useState, createContext } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import useAuth from "./../hooks/useAuth";
import { useMsg } from "./../hooks/MsgProvider";
import useInformation from "hooks/useInformation";
import { createTheme } from "@mui/material/styles";
import useSysParam from "hooks/UseSysParam";
import LoginPC from "view/pc/login/Login.pc";
import LoginMobile from "view/mobile/login/Login.mobile";
import RenderPcOrMobile from "./RenderPcOrMobile";
import MaintenanceDialog from "components/maintenanceDialog/MaintenanceDialog";
import { getLoginiconsInfo } from "api";
import Enum from "enum/Enum";
import { useHistory } from "react-router-dom";

export const LoginViewContext = createContext({});

const schema = yup.object().shape({
	email: yup.string().trim().required("Email is a required field"),
	pwd: yup.string().trim().required("Password is a required field"),
});

const theme = (themes) =>
	createTheme({
		...themes,
		components: {
			...themes.components,
			MuiInputBase: {
				styleOverrides: {
					input: {
						padding: 8,
					},
				},
			},
		},
	});

function Login() {
	let history = useHistory();

	const [email, setEmail] = useState("");
	const [showPassword, setShowPassword] = useState(false);

	const [isLoading, setIsLoading] = useState(false);
	const [isError, setIsError] = useState(false);
	const { setErrMsg } = useMsg();
	const [showLockTip, setShowLockTip] = useState(false);
	const [lockTipContent, setLockTipContent] = useState("");
	const [backgroundImgList, setBackgroundImgList] = useState([]);
	const [activeBackgroundIndex, setActiveBackgroundIndex] = useState(0);
	const intervalRef = React.useRef();
	const { paramTextValue: logoLink } = useSysParam("sys_logo_hyperlink", true);
	const { paramIntValue: noticeHeight } = useSysParam("important_notice_box_height", true);
	const { paramIntValue: inforHeight } = useSysParam("useful_information_height", true);
	const noticeHeightPx = `${noticeHeight}px`;
	const inforHeightPx = `${inforHeight}px`;
	const [loginRemainTime, setLoginRemainTime] = useState(0);
	const loginIntervalRef = React.useRef();
	const [displayMode, setDisplayMode] = useState("login");

	const handleFirstLogin = (msg) => {
		setLockTipContent(msg);
		setShowLockTip(true);
	};

	const { newLoginUser } = useAuth(handleFirstLogin);
	const {
		getValues,
		register,
		handleSubmit,
		control,
		formState: { errors },
	} = useForm({
		mode: "onChange",
		resolver: yupResolver(schema),
		defaultValues: {
			email: "",
			pwd: "",
		},
	});

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleMouseDownPassword = (event) => {
		event.preventDefault();
	};

	const handleChangeDisplay = (event, name) => {
		event.preventDefault();
		setDisplayMode(name);
	};
	const onSubmit = async (data) => {
		setIsLoading(true);
		setLoginRemainTime(5);

		await newLoginUser({ username: data.email, password: data.pwd })
			.catch((err) => {
				if (err.code === Enum.ERROR_CODE.NEED_TO_CHANGE_PASSWORD) {
					// for first time login, need to change password
					setLockTipContent(err.message);
					setShowLockTip(true);
				} else {
					setErrMsg(err);
					setIsError(true);
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const handleLockClose = () => {
		setShowLockTip(false);
		const changePwdData = { email: getValues("email").trim() };
		window.sessionStorage.setItem("changePwdData", JSON.stringify(changePwdData));
		history.push({
			pathname: "/change-pwd-first-login",
		});
	};

	const usefulInfo = useInformation("USEFUL_INFO", true).informations;
	const iptNotice = useInformation("IPT_NOTICE", true).informations;

	const getBackgroundImage = React.useCallback(() => {
		getLoginiconsInfo()
			.then((res) => {
				const imgsData = res.data.icons;
				setBackgroundImgList(imgsData);
				if (Array.isArray(imgsData) && imgsData.length > 0) {
					// activeBackgroundRef.current = imgsData[0];
					setActiveBackgroundIndex(0);
					clearInterval(intervalRef.current);
					intervalRef.current = setInterval(
						() => {
							setActiveBackgroundIndex((prev) => {
								if (imgsData.length <= prev + 1) {
									return 0;
								}
								return prev + 1;
							});
						},
						res.data.intervalTime * 1000 || 10000
					);
				}
			})
			.catch((err) => {
				if (err) {
					console.log(err);
				}
			});
	}, [backgroundImgList.length]);

	useEffect(() => {
		getBackgroundImage();
	}, []);

	useEffect(() => {
		if (loginRemainTime === 5) {
			clearInterval(loginIntervalRef.current);
			loginIntervalRef.current = setInterval(() => {
				setLoginRemainTime((prev) => prev - 1);
			}, 1000);
		}
		if (loginRemainTime === 0) {
			clearInterval(loginIntervalRef.current);
		}
	}, [loginRemainTime]);

	return (
		<LoginViewContext.Provider
			value={{
				register,
				isLoading,
				theme,
				backgroundImgList,
				noticeHeightPx,
				iptNotice,
				inforHeightPx,
				usefulInfo,
				activeBackgroundIndex,
				logoLink,
				handleSubmit,
				onSubmit,
				control,
				email,
				setEmail,
				errors,
				handleClickShowPassword,
				handleMouseDownPassword,
				showPassword,
				showLockTip,
				setShowLockTip,
				lockTipContent,
				handleLockClose,
				setErrMsg,
				isError,
				loginRemainTime,
				displayMode,
				handleChangeDisplay,
			}}
		>
			<RenderPcOrMobile pc={<LoginPC />} mobile={<LoginMobile />} />
			<MaintenanceDialog />
		</LoginViewContext.Provider>
	);
}
export default Login;
